import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Form, Input, Button, Alert, Steps, Tag } from 'antd';
import {
  SafetyCertificateOutlined,
  KeyOutlined,
  CheckCircleFilled
} from '@ant-design/icons';

// Approval MFA — step-up confirmation modal (L1 and L1+L2 flows).
//
// SEPARATE from the existing MfaVerificationModal — that one is a
// single-step modal designed for one-shot per-action TOTP. Approval
// MFA needs a small state machine (pending L1 → L1 ok → pending L2 →
// done) so we render a 2-step indicator inside this modal.
//
// Fully controlled by the `useApprovalMfa` hook (see hooks/useApprovalMfa.jsx).
// The hook owns the network calls, this component only renders the
// current step state + collects the 6-digit code.
//
// SECURITY
//   - We never store the code outside this component's local state.
//   - We never log the code.
//   - We never persist the code to localStorage / context / Redux.

const TITLES = {
  l1: 'L1 Approval MFA Required',
  l2: 'L2 Approval MFA Required'
};

const DESCRIPTIONS = {
  l1: 'Enter the 6-digit code from Google Authenticator to confirm this operation.',
  l2: 'Wait for a fresh code in Google Authenticator (next 30 sec) and enter it. The L2 code must be different from the L1 code.'
};

const VERIFY_LABELS = {
  l1: 'Verify L1 Approval',
  l2: 'Verify L2 Approval'
};

export default function ApprovalMfaModal({
  open,
  // 'l1' or 'l2' — which level the modal is currently collecting.
  step,
  // True if the resolved policy requires both L1 and L2.
  requiresL2,
  // Display label of the operation being approved (e.g. "Delete customer").
  operationLabel,
  // Optional resource summary line (e.g. "CUST-2001 · Sri Balaji Toughened Glass").
  resourceLabel,
  loading = false,
  errorText = null,
  onVerify,
  onCancel
}) {
  const [code, setCode] = useState('');
  const inputRef = useRef(null);

  // Reset code each time the modal opens or the step changes — a
  // previous L1 code must not leak into L2 input.
  useEffect(() => {
    if (open) {
      setCode('');
      const t = setTimeout(() => {
        try {
          inputRef.current?.focus?.();
        } catch (_e) { /* noop */ }
      }, 80);
      return () => clearTimeout(t);
    }
  }, [open, step]);

  const stepsItems = useMemo(() => {
    const l1Done = step === 'l2';
    return [
      {
        title: 'L1 Approval MFA',
        status: l1Done ? 'finish' : (step === 'l1' ? 'process' : 'wait'),
        icon: l1Done ? <CheckCircleFilled className="!text-emerald-500" /> : undefined
      },
      {
        title: 'L2 Approval MFA',
        status: !requiresL2
          ? 'wait'
          : (step === 'l2' ? 'process' : (l1Done ? 'process' : 'wait'))
      }
    ];
  }, [step, requiresL2]);

  const title = TITLES[step] || 'Approval Verification';
  const description = DESCRIPTIONS[step] || '';
  const verifyLabel = VERIFY_LABELS[step] || 'Verify';

  const isValid = /^\d{6}$/.test(String(code).trim());

  return (
    <Modal
      open={open}
      title={
        <div className="flex items-center gap-2">
          <SafetyCertificateOutlined className="text-brand-600" />
          <span>{title}</span>
        </div>
      }
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      width={460}
      maskClosable={!loading}
      closable={!loading}
    >
      <p className="text-sm text-ink-500 mt-1 mb-3">
        This operation requires additional approval verification.
      </p>

      {requiresL2 ? (
        <Steps
          size="small"
          current={step === 'l1' ? 0 : 1}
          items={stepsItems}
          className="mb-4"
        />
      ) : (
        <Tag color="blue" className="mb-3">L1 Approval MFA</Tag>
      )}

      {operationLabel ? (
        <div className="mb-3 rounded-lg border border-surface-200 bg-surface-50 px-3 py-2 text-sm">
          <div className="text-ink-500 text-[11px] uppercase tracking-wider font-semibold mb-0.5">
            Operation
          </div>
          <div className="text-ink-900 font-medium">{operationLabel}</div>
          {resourceLabel ? (
            <div className="text-ink-500 text-xs mt-0.5 font-mono">
              {resourceLabel}
            </div>
          ) : null}
        </div>
      ) : null}

      <p className="text-sm text-ink-700 mb-3">{description}</p>

      {errorText ? (
        <Alert
          showIcon
          type="error"
          message={errorText}
          className="mb-3"
        />
      ) : null}

      <Form
        layout="vertical"
        onFinish={() => {
          if (!isValid) return;
          if (typeof onVerify === 'function') onVerify(code);
        }}
      >
        <Form.Item label="6-digit verification code" required>
          <Input
            ref={inputRef}
            value={code}
            onChange={(e) => {
              const next = String(e.target.value || '').replace(/\D/g, '').slice(0, 6);
              setCode(next);
            }}
            placeholder="123456"
            maxLength={6}
            inputMode="numeric"
            autoComplete="one-time-code"
            spellCheck={false}
            prefix={<KeyOutlined className="text-ink-400" />}
            className="!text-base !tracking-[0.4em] !font-mono"
            disabled={loading}
          />
        </Form.Item>

        <div className="flex justify-end gap-2 mt-2">
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={!isValid || loading}
            className="!bg-[#1877F2] hover:!bg-blue-700 !border-0"
          >
            {verifyLabel}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
